<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="workflow-designer flex flex-col bg-slate-50 overflow-hidden" style="margin: -1.5rem; height: calc(100% + 3rem);">
    <!-- 顶部工具栏 -->
    <div class="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 shadow-sm shrink-0">
      <div class="flex items-center gap-3">
        <button @click="router.back()" class="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
          <el-icon :size="18"><ArrowLeft /></el-icon>
        </button>
        <el-divider direction="vertical" class="!my-1 !h-6" />
        <h1 class="text-base font-semibold text-slate-800">{{ workflowFormData.name || '未命名流程' }}</h1>
        <el-tag v-if="workflowStatus" :type="getStatusType(workflowStatus)" size="small">{{ getStatusLabel(workflowStatus) }}</el-tag>
      </div>
    </div>

    <!-- 步骤导航栏 -->
    <div class="bg-white border-b border-slate-200 shrink-0">
      <el-steps :active="currentStep" align-center>
        <el-step title="基本信息" />
        <el-step title="表单设计" />
        <el-step title="流程设计" />
      </el-steps>
    </div>

    <!-- 步骤内容区域 -->
    <div class="flex-1 overflow-hidden">
      <!-- 步骤1：基本信息 -->
      <div v-show="currentStep === 0" class="h-full overflow-auto p-6">
        <div class="max-w-2xl mx-auto">
          <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <el-icon :size="16" class="text-primary-600"><Edit /></el-icon>
              </div>
              <div>
                <h2 class="text-base font-semibold text-slate-800">流程基本信息</h2>
                <p class="text-xs text-slate-500">设置流程的名称、分类和描述</p>
              </div>
            </div>

            <el-form ref="basicInfoFormRef" :model="workflowFormData" :rules="basicInfoRules" label-position="top" class="space-y-3">
              <el-form-item label="流程编码" prop="code">
                <el-input
                  v-model="workflowFormData.code"
                  placeholder="请输入流程编码，如：LEAVE_PROCESS"
                  clearable
                />
              </el-form-item>
              <el-form-item label="流程名称" prop="name">
                <el-input
                  v-model="workflowFormData.name"
                  placeholder="请输入流程名称"
                  clearable
                />
              </el-form-item>
              <el-form-item label="流程分类" prop="categoryId">
                <el-select
                  v-model="workflowFormData.categoryId"
                  placeholder="请选择流程分类"
                  filterable
                  class="w-full"
                >
                  <el-option
                    v-for="cat in categoryList"
                    :key="cat.id"
                    :label="cat.name"
                    :value="cat.id"
                  />
                </el-select>
              </el-form-item>

              <el-form-item label="流程描述" prop="description">
                <el-input
                  v-model="workflowFormData.description"
                  type="textarea"
                  :rows="3"
                  placeholder="请输入流程描述，说明该流程的用途和适用范围"
                  maxlength="500"
                  show-word-limit
                />
              </el-form-item>

              <el-form-item label="流程图标">
                <div class="flex items-center gap-3">
                  <div class="w-12 h-12 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                    <el-icon :size="18" class="text-slate-400"><Plus /></el-icon>
                  </div>
                  <div class="text-sm text-slate-500">
                    <p>支持上传 PNG、JPG 格式</p>
                    <p>建议尺寸: 64x64 像素</p>
                  </div>
                </div>
              </el-form-item>
            </el-form>

            <div class="mt-4 flex justify-end gap-2">
              <el-button size="small" @click="router.back()">取消</el-button>
              <el-button type="primary" size="small" @click="nextStep">
                下一步：表单设计
                <el-icon class="ml-1"><Right /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 步骤2：表单设计 -->
      <div v-show="currentStep === 1" class="h-full flex flex-col">
        <FormDesigner :key="formDesignerKey" ref="formDesignerRef" :initial-form="workflowFormData.form" @save="onFormSave" />
        <div class="bg-white border-t border-slate-200 px-6 py-3 flex justify-between items-center shrink-0">
          <el-button @click="prevStep">
            <el-icon class="mr-1"><ArrowLeft /></el-icon>
            上一步
          </el-button>
          <div class="flex items-center gap-3">
            <span class="text-sm text-slate-500">
              表单字段: <strong class="text-slate-700">{{ formFieldCount }}</strong> 个
            </span>
            <el-button type="primary" @click="nextStep">
              下一步：流程设计
              <el-icon class="ml-1"><Right /></el-icon>
            </el-button>
          </div>
        </div>
      </div>

      <!-- 步骤3：流程设计 -->
      <div v-show="currentStep === 2" class="h-full flex flex-col">
        <!-- 流程图工具栏 -->
        <div class="h-12 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
          <div class="flex items-center gap-3">
            <el-button-group size="small">
              <el-button @click="undo" :disabled="!canUndo" title="撤销 (Ctrl+Z)">
                <el-icon><RefreshLeft /></el-icon>
              </el-button>
              <el-button @click="redo" :disabled="!canRedo" title="重做 (Ctrl+Y)">
                <el-icon><RefreshRight /></el-icon>
              </el-button>
            </el-button-group>
            <el-divider direction="vertical" class="!my-1 !h-5" />
            <div class="flex items-center bg-slate-100 rounded-lg p-0.5">
              <button
                @click="zoomOut"
                class="p-1.5 rounded hover:bg-white hover:shadow-sm transition-all"
                title="缩小"
              >
                <el-icon><Remove /></el-icon>
              </button>
              <span class="px-2 text-xs font-medium text-slate-600 min-w-[48px] text-center">{{ Math.round(zoom * 100) }}%</span>
              <button
                @click="zoomIn"
                class="p-1.5 rounded hover:bg-white hover:shadow-sm transition-all"
                title="放大"
              >
                <el-icon><Plus /></el-icon>
              </button>
            </div>
            <el-button size="small" @click="fitView" title="适应画布">
              <el-icon><FullScreen /></el-icon>
            </el-button>
          </div>
          <div class="flex items-center gap-1.5">
            <el-button v-permission="'workflow:template:edit'" size="small" type="primary" @click="save" :loading="saving">
              <el-icon><Check /></el-icon>
              <span class="ml-1">保存流程</span>
            </el-button>
          </div>
        </div>

        <!-- 步骤导航栏 -->
        <div class="h-12 bg-white border-b border-slate-200 px-4 flex items-center justify-between shrink-0">
          <el-button @click="prevStep">
            <el-icon class="mr-1"><ArrowLeft /></el-icon>
            上一步
          </el-button>
        </div>

        <!-- 主内容区域 -->
        <div class="flex-1 flex overflow-hidden">
      <!-- 左侧：节点面板 -->
      <div class="w-56 bg-white border-r border-slate-200 flex flex-col shadow-sm shrink-0">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wider">流程节点</h3>
        </div>
        <div class="flex-1 overflow-y-auto p-3">
          <div class="space-y-2">
            <div
              v-for="nodeType in nodeTypes"
              :key="nodeType.type"
              class="group flex items-center gap-3 px-3 py-2.5 bg-slate-50 hover:bg-white border border-slate-200 hover:border-primary-300 rounded-lg cursor-grab active:cursor-grabbing transition-all duration-200 hover:shadow-md"
              :class="[`node-type-${nodeType.type.toLowerCase()}`]"
              draggable="true"
              @dragstart="onDragStart($event, nodeType)"
            >
              <div class="w-9 h-9 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105" :class="nodeType.bgClass">
                <el-icon :size="18" :class="nodeType.iconClass">
                  <component :is="nodeType.icon" />
                </el-icon>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-slate-700 group-hover:text-slate-900">{{ nodeType.label }}</div>
                <div class="text-xs text-slate-400 truncate">{{ nodeType.description }}</div>
              </div>
              <el-icon class="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"><MoreFilled /></el-icon>
            </div>
          </div>

          <!-- 帮助提示 -->
          <div class="mt-6 p-3 bg-primary-50 rounded-lg border border-primary-100">
            <div class="flex items-start gap-2">
              <el-icon class="text-primary-500 mt-0.5" :size="14"><InfoFilled /></el-icon>
              <div class="text-xs text-primary-700">
                <p class="font-medium mb-1">使用提示</p>
                <p class="text-primary-600">从左侧拖拽节点到画布，连接节点创建流程</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 中间：画布容器 -->
      <div class="flex-1 flex flex-col min-h-0 bg-slate-100/50">
        <!-- 画布 -->
        <div
          ref="canvasContainerRef"
          class="flex-1 overflow-hidden relative"
          :class="panMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'"
          @drop="onDrop"
          @dragover.prevent
          @mousedown="onCanvasMouseDown"
          @contextmenu.prevent="onCanvasContextMenu"
          @wheel="onCanvasWheel"
        >
          <!-- 画布工具栏 - 放在画布容器左上角 -->
          <div class="absolute top-4 left-4 z-20 flex flex-col gap-2">
            <div class="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200/60 p-1.5 flex flex-col gap-1">
              <el-tooltip content="拖拽画布 (按住空格键)" placement="right">
                <button
                  @click="togglePanMode"
                  class="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-all"
                  :class="{ 'bg-primary-100 text-primary-600': panMode }"
                >
                  <el-icon><Rank /></el-icon>
                </button>
              </el-tooltip>
              <el-divider class="!my-1" />
              <el-tooltip content="自动布局" placement="right">
                <button class="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-all" @click="autoLayout">
                  <el-icon><Grid /></el-icon>
                </button>
              </el-tooltip>
              <el-tooltip content="缩小视图" placement="right">
                <button class="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-all" @click="zoomOut">
                  <el-icon><ZoomOut /></el-icon>
                </button>
              </el-tooltip>
              <el-tooltip content="放大视图" placement="right">
                <button class="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-all" @click="zoomIn">
                  <el-icon><ZoomIn /></el-icon>
                </button>
              </el-tooltip>
            </div>
          </div>

          <div
            ref="canvasRef"
            class="absolute inset-0 transition-transform duration-75"
            :style="{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
              transformOrigin: '0 0'
            }"
            @click="onCanvasClick"
          >
            <!-- 网格背景 -->
            <svg class="absolute inset-0 pointer-events-none" style="width: 100%; height: 100%;">
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="1" cy="1" r="1" fill="#94a3b8" opacity="0.4" />
                </pattern>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>

            <!-- SVG连线层 -->
            <svg class="absolute inset-0 pointer-events-none" style="width: 100%; height: 100%; overflow: visible;">
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="10"
                  refX="8"
                  refY="5"
                  orient="auto"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
                </marker>
                <marker
                  id="arrowhead-selected"
                  markerWidth="10"
                  markerHeight="10"
                  refX="8"
                  refY="5"
                  orient="auto"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
                </marker>
              </defs>
              <!-- 临时连线 -->
              <path
                v-if="tempEdge"
                :d="getTempEdgePath()"
                stroke="#3b82f6"
                stroke-width="2"
                fill="none"
                stroke-dasharray="6,4"
                class="transition-all duration-75"
              />
              <!-- 已有连线 -->
              <g>
                <template v-for="(edge, edgeIndex) in edges" :key="getEdgeKey(edge, edgeIndex)">
                  <path
                    :d="getEdgePath(edge)"
                    :stroke="isEdgeSelected(edge) ? '#3b82f6' : '#94a3b8'"
                    :stroke-width="isEdgeSelected(edge) ? 2.5 : 1.5"
                    fill="none"
                    :marker-end="isEdgeSelected(edge) ? 'url(#arrowhead-selected)' : 'url(#arrowhead)'"
                    class="flow-edge pointer-events-auto cursor-pointer transition-all duration-200"
                    :class="isEdgeSelected(edge) ? 'opacity-100' : 'opacity-70 hover:opacity-100 hover:stroke-slate-500'"
                    @click.stop="selectEdge(edge)"
                    @contextmenu.prevent.stop="onEdgeContextMenu($event, edge)"
                  />
                  <!-- 连线标签 -->
                  <g v-if="edge.label" class="pointer-events-auto cursor-pointer" @click.stop="selectEdge(edge)">
                    <rect
                      :x="getEdgeLabelPosition(edge).x - 20"
                      :y="getEdgeLabelPosition(edge).y - 10"
                      width="40"
                      height="20"
                      rx="4"
                      fill="white"
                      stroke="#e2e8f0"
                      class="transition-all duration-200"
                      :class="{ 'stroke-primary-300 fill-primary-50': isEdgeSelected(edge) }"
                    />
                    <text
                      :x="getEdgeLabelPosition(edge).x"
                      :y="getEdgeLabelPosition(edge).y + 4"
                      text-anchor="middle"
                      class="text-[10px] font-medium pointer-events-none transition-colors duration-200"
                      :class="isEdgeSelected(edge) ? 'fill-primary-600' : 'fill-slate-500'"
                    >
                      {{ edge.label }}
                    </text>
                  </g>
                </template>
              </g>
            </svg>

            <!-- 节点层 -->
            <div
              v-for="node in nodes"
              :key="node.id"
              :data-node-id="node.id"
              class="absolute transition-all duration-150"
              :style="{
                left: node.x + 'px',
                top: node.y + 'px',
                zIndex: selectedNode?.id === node.id ? 100 : 10
              }"
              @mousedown.stop="onNodeMouseDown($event, node)"
              @click.stop="selectNode(node)"
              @contextmenu.prevent.stop="onNodeContextMenu($event, node)"
            >
              <div
                class="flow-node bg-white rounded-xl shadow-sm border-2 transition-all duration-200"
                :class="[
                  getNodeClass(node),
                  {
                    'ring-4 ring-primary-200/50 shadow-lg scale-[1.02]': selectedNode?.id === node.id,
                    'hover:shadow-lg hover:scale-[1.01]': selectedNode?.id !== node.id
                  }
                ]"
                :style="{ minWidth: getNodeWidth(node) + 'px' }"
              >
                <!-- 节点头部 -->
                <div class="node-header px-4 py-2.5 rounded-t-lg flex items-center gap-2" :class="getNodeHeaderClass(node)">
                  <el-icon :size="14">
                    <component :is="getNodeIcon(node.type)" />
                  </el-icon>
                  <span class="text-sm font-semibold truncate max-w-[120px]">{{ node.label }}</span>
                  <el-tag v-if="node.code" size="small" class="ml-auto !bg-white/20 !border-white/30 !text-inherit" :hit="false">{{ node.code }}</el-tag>
                </div>

                <!-- 节点内容 -->
                <div v-if="hasNodeContent(node)" class="px-4 py-3 text-xs space-y-2 bg-slate-50/50 rounded-b-lg">
                  <!-- 审批节点内容 -->
                  <div v-if="node.type === 'APPROVAL'" class="flex items-center gap-2 text-slate-600">
                    <el-icon :size="12" class="text-slate-400"><User /></el-icon>
                    <span>{{ getApproverText(node) }}</span>
                  </div>
                  <!-- 条件节点内容 -->
                  <div v-if="node.type === 'CONDITION' && node.branches && node.branches.length > 0" class="space-y-1.5">
                    <div v-for="(branch, idx) in node.branches.slice(0, 3)" :key="idx" class="flex items-center gap-1.5">
                      <span class="w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-[9px] font-bold">{{ idx + 1 }}</span>
                      <span class="text-slate-600 truncate">{{ branch?.label || `分支${idx + 1}` }}</span>
                    </div>
                    <div v-if="node.branches.length > 3" class="text-slate-400 text-xs pl-5">
                      +{{ node.branches.length - 3 }} 更多
                    </div>
                  </div>
                  <!-- 抄送节点内容 -->
                  <div v-if="node.type === 'CC'" class="flex items-center gap-2 text-slate-600">
                    <el-icon :size="12" class="text-slate-400"><Message /></el-icon>
                    <span>{{ getCcText(node) }}</span>
                  </div>
                </div>

                <!-- 连接点 -->
                <!-- 普通节点：单个出口点 -->
                <div
                  v-if="node.type !== 'END' && node.type !== 'CONDITION'"
                  class="node-handle node-handle-out"
                  @mousedown.stop="onHandleMouseDown($event, node, 'out')"
                ></div>

                <!-- 条件节点：每个分支一个出口点 -->
                <template v-if="node.type === 'CONDITION' && node.branches">
                  <div
                    v-for="(branch, idx) in node.branches"
                    :key="`branch-${idx}`"
                    class="node-handle node-handle-branch"
                    :class="`branch-handle-${idx}`"
                    :style="getBranchHandleStyle(node, idx)"
                    :title="`分支: ${branch.label || '分支' + (idx + 1)}`"
                    @mousedown.stop="onHandleMouseDown($event, node, 'out', idx)"
                  >
                    <span class="branch-handle-label">{{ idx + 1 }}</span>
                  </div>
                </template>

                <!-- 入口点（所有非开始节点） -->
                <div
                  v-if="node.type !== 'START'"
                  class="node-handle node-handle-in"
                  @mousedown.stop="onHandleMouseDown($event, node, 'in')"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部状态栏 -->
        <div class="h-8 bg-white border-t border-slate-200 px-4 flex items-center justify-between text-xs text-slate-500 shrink-0">
          <div class="flex items-center gap-4">
            <span>节点: <strong class="text-slate-700">{{ nodes.length }}</strong></span>
            <span>连线: <strong class="text-slate-700">{{ edges.length }}</strong></span>
          </div>
          <div class="flex items-center gap-2">
            <span v-if="selectedNode" class="text-primary-600">已选择: {{ selectedNode.label }}</span>
            <span v-else-if="selectedEdge" class="text-primary-600">已选择连线</span>
            <span v-else>点击画布空白处取消选择</span>
          </div>
        </div>
      </div>

      <!-- 右侧：属性面板 -->
      <div v-if="selectedNode || selectedEdge" class="w-80 bg-white border-l border-slate-200 flex flex-col shadow-sm shrink-0">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between shrink-0">
          <div class="flex items-center gap-2">
            <el-icon :size="16" class="text-slate-400"><Setting /></el-icon>
            <h3 class="text-sm font-semibold text-slate-700">
              {{ selectedNode ? '节点属性' : '连线属性' }}
            </h3>
          </div>
          <button @click="closePropertyPanel" class="p-1 rounded hover:bg-slate-200 transition-colors">
            <el-icon :size="14"><Close /></el-icon>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto">
          <!-- 节点属性表单 -->
          <el-form v-if="selectedNode" label-position="top" class="p-4 space-y-4">
            <div class="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center" :class="getNodeBgClass(selectedNode.type)">
                <el-icon :size="20" :class="getNodeIconClass(selectedNode.type)">
                  <component :is="getNodeIcon(selectedNode.type)" />
                </el-icon>
              </div>
              <div>
                <div class="font-medium text-slate-800">{{ selectedNode.label }}</div>
                <div class="text-xs text-slate-500">{{ getNodeTypeLabel(selectedNode.type) }}</div>
              </div>
            </div>

            <el-form-item label="节点ID" class="!mb-3">
              <el-input :model-value="selectedNode.id" disabled size="small" />
            </el-form-item>
            <el-form-item label="节点编码" class="!mb-3">
              <el-input v-model="selectedNode.code" placeholder="请输入节点编码" size="small" />
            </el-form-item>
            <el-form-item label="节点名称" class="!mb-3">
              <el-input v-model="selectedNode.label" placeholder="请输入节点名称" size="small" />
            </el-form-item>

            <!-- 审批节点配置 -->
            <template v-if="selectedNode.type === 'APPROVAL'">
              <el-divider content-position="left" class="!my-4">审批配置</el-divider>
              <el-form-item label="审批人方式" class="!mb-3">
                <el-select v-model="selectedNode.config.approverMode" class="w-full" size="small" @change="onApproverModeChange">
                  <el-option label="指定用户" value="USER" />
                  <el-option label="指定角色" value="ROLE" />
                  <el-option label="部门负责人" value="DEPT_LEADER" />
                  <el-option label="发起人主管" value="INITIATOR_MANAGER" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="['USER', 'ROLE'].includes(selectedNode.config.approverMode)" label="审批人值" class="!mb-3">
                <el-select
                  v-if="selectedNode.config.approverMode === 'USER'"
                  v-model="approverValueArray"
                  placeholder="请选择审批人"
                  filterable
                  clearable
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  value-key="id"
                  class="w-full"
                  size="small"
                  :loading="userLoading"
                >
                  <el-option
                    v-for="user in userList"
                    :key="user.id"
                    :label="user.label"
                    :value="user"
                  />
                </el-select>
                <el-select
                  v-else-if="selectedNode.config.approverMode === 'ROLE'"
                  v-model="approverValueArray"
                  placeholder="请选择角色"
                  filterable
                  clearable
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  value-key="id"
                  class="w-full"
                  size="small"
                  :loading="roleLoading"
                >
                  <el-option
                    v-for="role in roleList"
                    :key="role.id"
                    :label="role.label"
                    :value="role"
                  />
                </el-select>
              </el-form-item>
              <div v-else class="mb-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <el-icon class="mr-1"><InfoFilled /></el-icon>
                {{ selectedNode.config.approverMode === 'DEPT_LEADER' ? '自动获取发起人所在部门的负责人' : '自动获取发起人的直接主管' }}
              </div>
              <el-form-item label="审批模式" class="!mb-3">
                <el-select v-model="selectedNode.config.approvalMode" class="w-full" size="small">
                  <el-option label="或签(任一同意)" value="ANY" />
                  <el-option label="会签(全部同意)" value="ALL" />
                  <el-option label="顺序审批" value="SEQUENTIAL" />
                </el-select>
              </el-form-item>
              <div class="grid grid-cols-2 gap-3">
                <el-form-item label="超时时间(小时)" class="!mb-3">
                  <el-input-number
                    v-model="selectedNode.config.timeoutHours"
                    :min="1"
                    :max="720"
                    class="w-full"
                    size="small"
                  />
                </el-form-item>
              </div>
              <div class="flex items-center gap-4">
                <el-form-item label="允许驳回" class="!mb-0 flex-1">
                  <el-switch v-model="selectedNode.config.allowReject" />
                </el-form-item>
                <el-form-item label="允许转办" class="!mb-0 flex-1">
                  <el-switch v-model="selectedNode.config.allowDelegate" />
                </el-form-item>
              </div>
            </template>

            <!-- 条件分支配置 -->
            <template v-if="selectedNode.type === 'CONDITION' && selectedNode.branches">
              <el-divider content-position="left" class="!my-4">条件配置</el-divider>

              <!-- 字段提示 -->
              <div v-if="availableFields.length === 0" class="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div class="flex items-start gap-2">
                  <el-icon class="text-amber-500 mt-0.5"><WarningFilled /></el-icon>
                  <div class="flex-1 text-xs">
                    <p class="font-medium text-amber-800 mb-0.5">暂无可用字段</p>
                    <p class="text-amber-600">请先点击工具栏的"表单设计"按钮创建表单字段</p>
                  </div>
                </div>
              </div>
              <div v-else class="mb-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div class="flex items-center gap-2 text-xs text-emerald-700">
                  <el-icon class="text-emerald-500"><CircleCheck /></el-icon>
                  <span>已加载 <strong>{{ availableFields.length }}</strong> 个表单字段</span>
                </div>
              </div>

              <div
                v-for="(branch, index) in selectedNode.branches"
                :key="index"
                class="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                    <span class="w-5 h-5 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-[10px] font-bold">{{ index + 1 }}</span>
                    分支 {{ index + 1 }}
                  </span>
                  <el-button
                    v-if="selectedNode.branches.length > 1"
                    type="danger"
                    link
                    size="small"
                    @click="removeBranch(index)"
                  >
                    删除
                  </el-button>
                </div>
                <el-form-item label="分支名称" label-width="70px" class="!mb-2">
                  <el-input v-model="branch.label" placeholder="如：金额>=1000" size="small" />
                </el-form-item>
                <template v-if="branch.condition">
                  <el-form-item label="条件字段" label-width="70px" class="!mb-2">
                    <el-select
                      v-model="branch.condition.field"
                      filterable
                      allow-create
                      placeholder="选择或输入字段"
                      size="small"
                      class="w-full"
                    >
                      <el-option
                        v-for="field in availableFields || []"
                        :key="field.key"
                        :label="field.label"
                        :value="field.key"
                      />
                    </el-select>
                  </el-form-item>
                  <div class="grid grid-cols-2 gap-2">
                    <el-form-item label="运算符" label-width="70px" class="!mb-2">
                      <el-select v-model="branch.condition.operator" size="small" class="w-full">
                        <el-option label="等于" value="EQ" />
                        <el-option label="不等于" value="NEQ" />
                        <el-option label="大于" value="GT" />
                        <el-option label="大于等于" value="GTE" />
                        <el-option label="小于" value="LT" />
                        <el-option label="小于等于" value="LTE" />
                        <el-option label="包含" value="CONTAINS" />
                        <el-option label="不包含" value="NOT_CONTAINS" />
                      </el-select>
                    </el-form-item>
                    <el-form-item label="条件值" label-width="70px" class="!mb-2">
                      <el-input
                        v-model="branch.condition.value"
                        placeholder="条件值"
                        size="small"
                      />
                    </el-form-item>
                  </div>
                </template>
              </div>
              <el-button type="primary" link @click="addBranch" class="w-full">
                <el-icon><Plus /></el-icon>
                添加分支
              </el-button>
            </template>

            <!-- 抄送节点配置 -->
            <template v-if="selectedNode.type === 'CC'">
              <el-divider content-position="left" class="!my-4">抄送配置</el-divider>
              <el-form-item label="抄送方式" class="!mb-3">
                <el-select v-model="selectedNode.config.approverMode" class="w-full" size="small" @change="onApproverModeChange">
                  <el-option label="指定用户" value="USER" />
                  <el-option label="指定角色" value="ROLE" />
                  <el-option label="部门负责人" value="DEPT_LEADER" />
                  <el-option label="动态指定" value="DYNAMIC" />
                </el-select>
              </el-form-item>
              <el-form-item label="抄送值" class="!mb-3">
                <el-select
                  v-if="selectedNode.config.approverMode === 'USER'"
                  v-model="selectedNode.config.approverValue"
                  placeholder="请选择抄送人"
                  filterable
                  clearable
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  class="w-full"
                  size="small"
                  :loading="userLoading"
                >
                  <el-option
                    v-for="user in userList"
                    :key="user.id"
                    :label="user.label"
                    :value="user.id"
                  />
                </el-select>
                <el-select
                  v-else-if="selectedNode.config.approverMode === 'ROLE'"
                  v-model="selectedNode.config.approverValue"
                  placeholder="请选择角色"
                  filterable
                  clearable
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  class="w-full"
                  size="small"
                  :loading="roleLoading"
                >
                  <el-option
                    v-for="role in roleList"
                    :key="role.id"
                    :label="role.label"
                    :value="role.id"
                  />
                </el-select>
                <el-input
                  v-else
                  v-model="selectedNode.config.approverValue"
                  :placeholder="selectedNode.config.approverMode === 'DEPT_LEADER' ? '部门ID' : '动态指定'"
                  size="small"
                />
              </el-form-item>
              <el-form-item label="通知方式" class="!mb-3">
                <el-select v-model="selectedNode.config.notifyMode" class="w-full" size="small">
                  <el-option label="站内信+邮件" value="BOTH" />
                  <el-option label="仅站内信" value="INTERNAL" />
                  <el-option label="仅邮件" value="EMAIL" />
                </el-select>
              </el-form-item>
            </template>

            <el-divider class="!my-4" />

            <el-button type="danger" size="small" class="w-full" @click="deleteNode">
              <el-icon><Delete /></el-icon>
              删除节点
            </el-button>
          </el-form>

          <!-- 连线属性表单 -->
          <el-form v-if="selectedEdge" label-position="top" class="p-4 space-y-4">
            <div class="p-3 bg-slate-50 rounded-lg">
              <div class="flex items-center justify-between text-sm">
                <span class="text-slate-500">连线ID</span>
                <span class="text-slate-700 font-mono text-xs">{{ selectedEdge.id }}</span>
              </div>
            </div>

            <el-form-item label="连线标签" class="!mb-3">
              <el-input v-model="selectedEdge.label" placeholder="如：是/否、通过/拒绝" size="small" />
            </el-form-item>

            <div class="grid grid-cols-2 gap-3">
              <el-form-item label="源节点" class="!mb-0">
                <el-input :model-value="getNodeLabel(selectedEdge.source)" disabled size="small" />
              </el-form-item>
              <el-form-item label="目标节点" class="!mb-0">
                <el-input :model-value="getNodeLabel(selectedEdge.target)" disabled size="small" />
              </el-form-item>
            </div>

            <el-divider class="!my-4" />

            <el-button type="danger" size="small" class="w-full" @click="deleteEdge">
              <el-icon><Delete /></el-icon>
              删除连线
            </el-button>
          </el-form>
        </div>
      </div>
    </div>

    <!-- 右键菜单 -->
    <Teleport to="body">
      <Transition name="context-menu">
        <div
          v-if="contextMenu.visible"
          class="fixed bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 min-w-[140px] overflow-hidden"
          :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
          @click="closeContextMenu"
        >
          <template v-if="contextMenu.type === 'node'">
            <button
              class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
              @click="copyNode"
            >
              <el-icon class="text-slate-400"><DocumentCopy /></el-icon>
              <span class="text-slate-700">复制节点</span>
            </button>
            <div class="h-px bg-slate-100 my-1"></div>
            <button
              class="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2.5 transition-colors"
              @click="deleteNode"
            >
              <el-icon class="text-red-500"><Delete /></el-icon>
              <span class="text-red-600">删除节点</span>
            </button>
          </template>
          <template v-if="contextMenu.type === 'edge'">
            <button
              class="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2.5 transition-colors"
              @click="deleteEdge"
            >
              <el-icon class="text-red-500"><Delete /></el-icon>
              <span class="text-red-600">删除连线</span>
            </button>
          </template>
          <template v-if="contextMenu.type === 'canvas'">
            <button
              v-if="copiedNode"
              class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
              @click="pasteNode"
            >
              <el-icon class="text-slate-400"><DocumentAdd /></el-icon>
              <span class="text-slate-700">粘贴节点</span>
            </button>
            <button
              class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
              @click="autoLayout"
            >
              <el-icon class="text-slate-400"><Connection /></el-icon>
              <span class="text-slate-700">自动布局</span>
            </button>
          </template>
        </div>
      </Transition>
    </Teleport>

    <!-- 表单设计器对话框 -->
    <el-dialog
      v-model="formDesignerVisible"
      title="表单设计器"
      width="90%"
      :close-on-click-modal="false"
      top="5vh"
      :destroy-on-close="true"
      :modal="true"
      append-to-body
    >
      <FormDesigner :key="formDesignerKey" ref="formDesignerRef" :initial-form="workflowFormData.form" @save="onFormSave" />
      <template #footer>
        <el-button @click="formDesignerVisible = false">关闭</el-button>
        <el-button type="primary" @click="applyFormFields">应用表单字段</el-button>
      </template>
    </el-dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Plus,
  Check,
  Connection,
  Document,
  Close,
  RefreshLeft,
  RefreshRight,
  ZoomIn,
  ZoomOut,
  FullScreen,
  Grid,
  User,
  Message,
  Delete,
  DocumentCopy,
  DocumentAdd,
  ArrowLeft,
  Remove,
  Rank,
  MoreFilled,
  InfoFilled,
  WarningFilled,
  CircleCheck,
  Setting,
  Edit,
  Right
} from '@element-plus/icons-vue'
import FormDesigner from './FormDesigner.vue'
import { getWorkflowCategories, createTemplate, updateTemplate, getTemplateDetail } from '@/api/flow'
import { getUserList } from '@/api/user'
import { getRoles } from '@/api/role'

const route = useRoute()
const router = useRouter()

// 节点类型定义
const nodeTypes = [
  {
    type: 'START',
    label: '开始',
    description: '流程起点',
    icon: Plus,
    bgClass: 'bg-green-100',
    iconClass: 'text-green-600'
  },
  {
    type: 'APPROVAL',
    label: '审批',
    description: '审批节点',
    icon: Check,
    bgClass: 'bg-blue-100',
    iconClass: 'text-blue-600'
  },
  {
    type: 'CONDITION',
    label: '条件分支',
    description: '条件判断',
    icon: Connection,
    bgClass: 'bg-orange-100',
    iconClass: 'text-orange-600'
  },
  {
    type: 'CC',
    label: '抄送',
    description: '知会抄送',
    icon: Document,
    bgClass: 'bg-purple-100',
    iconClass: 'text-purple-600'
  },
  {
    type: 'END',
    label: '结束',
    description: '流程终点',
    icon: Close,
    bgClass: 'bg-red-100',
    iconClass: 'text-red-600'
  }
]

// 画布引用
const canvasRef = ref(null)
const canvasContainerRef = ref(null)

// 流程基本信息
const workflowId = ref(route.query.id || null)
const workflowStatus = ref('draft')

// 步骤控制
const currentStep = ref(0)
const basicInfoFormRef = ref(null)

// 流程表单数据（整合三步）
const workflowFormData = reactive({
  code: '',
  name: '',
  categoryId: null,
  description: '',
  icon: '',
  form: { name: '', fields: [] },
  nodes: [],
  edges: []
})

// 基本信息验证规则
const basicInfoRules = {
  code: [
    { required: true, message: '请输入流程编码', trigger: 'blur' },
    { pattern: /^[A-Z][A-Z0-9_]*$/, message: '流程编码必须是大写字母、数字或下划线，且以字母开头', trigger: 'blur' }
  ],
  name: [
    { required: true, message: '请输入流程名称', trigger: 'blur' },
    { min: 2, max: 50, message: '流程名称长度为2-50个字符', trigger: 'blur' }
  ],
  categoryId: [
    { required: true, message: '请选择流程分类', trigger: 'change' }
  ]
}

// 分类列表
const categoryList = ref([])
const categoryLoading = ref(false)

// 表单字段数量
const formFieldCount = computed(() => {
  return workflowFormData.form?.fields?.length || 0
})

// 加载分类列表
async function loadCategoryList() {
  categoryLoading.value = true
  try {
    const res = await getWorkflowCategories()
    const list = res.data || res || []
    // 统一字段名，添加 name 字段方便模板使用
    categoryList.value = list.map((cat) => ({
      ...cat,
      id: cat.id,
      name: cat.categoryName
    }))
  } catch (error) {
    console.error('加载分类失败:', error)
    ElMessage.error('加载分类失败')
  } finally {
    categoryLoading.value = false
  }
}

// 下一步
async function nextStep() {
  if (currentStep.value === 0) {
    // 验证基本信息
    if (!basicInfoFormRef.value) return
    try {
      await basicInfoFormRef.value.validate()
    } catch {
      return
    }
  }

  // 从步骤2进入步骤3时，同步表单字段到可用字段列表
  if (currentStep.value === 1 && currentStep.value < 2) {
    // 如果 FormDesigner ref 存在，获取最新字段数据
    if (formDesignerRef.value) {
      const fieldList = formDesignerRef.value.getFieldList()
      if (fieldList && fieldList.length > 0) {
        availableFields.value = fieldList
        console.log('同步表单字段到条件分支:', availableFields.value)
      }
    }
    // 同时也更新 workflowFormData.form
    if (formDesignerRef.value) {
      workflowFormData.form = formDesignerRef.value.getFormData()
    }
  }

  if (currentStep.value < 2) {
    currentStep.value++
  }
}

// 上一步
function prevStep() {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// 画布状态
const zoom = ref(1)
const panOffset = reactive({ x: 0, y: 0 })
const panMode = ref(false)
let spaceKeyPressed = false // 全局空格键状态
const isPanning = ref(false)
const panStart = reactive({ x: 0, y: 0 })

// 键盘事件处理函数
function onKeyDown(e) {
  if (e.code === 'Space' && !e.repeat) {
    e.preventDefault()
    spaceKeyPressed = true
  }
}

function onKeyUp(e) {
  if (e.code === 'Space') {
    e.preventDefault()
    spaceKeyPressed = false
  }
}

function onWindowBlur() {
  spaceKeyPressed = false
}

// 节点和连线
const nodes = ref([])
const edges = ref([])
const selectedNode = ref(null)
const selectedEdge = ref(null)

function hasEdgeId(edge) {
  return edge && edge.id !== undefined && edge.id !== null && `${edge.id}` !== ''
}

function isSameEdge(a, b) {
  if (!a || !b) return false

  if (hasEdgeId(a) && hasEdgeId(b)) {
    return String(a.id) === String(b.id)
  }

  return a.source === b.source &&
    a.target === b.target &&
    (a.branchIndex ?? null) === (b.branchIndex ?? null)
}

function getEdgeKey(edge, index) {
  if (hasEdgeId(edge)) {
    return `edge-id-${edge.id}`
  }

  const branch = edge?.branchIndex ?? 'main'
  return `edge-${edge?.source || 'unknown'}-${edge?.target || 'unknown'}-${branch}-${index}`
}

function isEdgeSelected(edge) {
  return isSameEdge(edge, selectedEdge.value)
}

function normalizeEdge(edge, index) {
  if (!edge || typeof edge !== 'object') return null

  const source = edge.source ?? edge.sourceNodeId ?? edge.from ?? edge.fromNodeId
  const target = edge.target ?? edge.targetNodeId ?? edge.to ?? edge.toNodeId
  if (!source || !target) return null

  return {
    ...edge,
    id: hasEdgeId(edge)
      ? String(edge.id)
      : `edge_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 7)}`,
    source,
    target,
    label: edge.label || ''
  }
}

function normalizeEdges(rawEdges) {
  if (!Array.isArray(rawEdges)) return []
  return rawEdges.map((edge, index) => normalizeEdge(edge, index)).filter(Boolean)
}

// 审批人/抄送方式变更时加载数据
function onApproverModeChange(mode) {
  if (mode === 'USER') {
    loadUserList()
  } else if (mode === 'ROLE') {
    loadRoleList()
  }
}

// 审批人值计算属性（支持多选，存储对象以显示名称）
const approverValueArray = computed({
  get() {
    const value = selectedNode.value?.config?.approverValue
    const mode = selectedNode.value?.config?.approverMode
    if (!value) return []

    if (Array.isArray(value)) return value

    // 字符串转对象数组
    const ids = value.split(',').filter(v => v)
    if (mode === 'USER') {
      return ids.map(id => {
        const user = userList.value.find(u => u.id == id || u.value == id)
        return user ? { id: user.id, label: user.label, value: user.id } : { id, label: id, value: id }
      })
    } else if (mode === 'ROLE') {
      return ids.map(id => {
        const role = roleList.value.find(r => r.id == id || r.value == id)
        return role ? { id: role.id, label: role.label, value: role.id } : { id, label: id, value: id }
      })
    }
    return ids.map(id => ({ id, label: id, value: id }))
  },
  set(val) {
    if (Array.isArray(val)) {
      // 提取 ID 用逗号拼接保存
      selectedNode.value.config.approverValue = val.map(item => item.id || item).join(',')
    } else {
      selectedNode.value.config.approverValue = val
    }
  }
})

// 拖拽状态
const isDraggingNode = ref(false)
const dragNode = ref(null)
const dragStart = reactive({ x: 0, y: 0 })

// 连线创建状态
const tempEdge = ref(null)
const connectingNode = ref(null)
const connectingHandle = ref(null)

// 历史记录
const historyStack = ref([])
const historyIndex = ref(-1)

// 右键菜单
const contextMenu = reactive({
  visible: false,
  x: 0,
  y: 0,
  type: null,
  target: null
})

// 复制粘贴
const copiedNode = ref(null)

// 表单设计器
const formDesignerVisible = ref(false)
const formDesignerRef = ref(null)
const formDesignerKey = ref(0) // 用于强制刷新表单设计器

// 可用字段（从表单字段动态生成）
const availableFields = ref([])

// 用户和角色列表
const userList = ref([])
const roleList = ref([])
const userLoading = ref(false)
const roleLoading = ref(false)

// 对话框
const saving = ref(false)

// 加载用户列表
async function loadUserList() {
  userLoading.value = true
  try {
    const res = await getUserList({ page: 1, size: 1000 })
    console.log('用户列表响应:', res)
    // res 就是分页对象 { records: [...] }
    const list = res.records || []
    console.log('用户列表数据:', list)
    userList.value = list.map(user => ({
      id: user.id,
      label: user.displayName || user.username,
      value: user.id
    }))
    console.log('处理后用户列表:', userList.value)
  } catch (error) {
    console.error('加载用户列表失败:', error)
  } finally {
    userLoading.value = false
  }
}

// 加载角色列表
async function loadRoleList() {
  roleLoading.value = true
  try {
    const res = await getRoles({ status: 0 })
    console.log('角色列表响应:', res)
    // res 是数组格式
    const list = Array.isArray(res) ? res : (res.records || [])
    console.log('角色列表数据:', list)
    roleList.value = list.map(role => ({
      id: role.id,
      label: role.name,
      value: role.id
    }))
    console.log('处理后角色列表:', roleList.value)
  } catch (error) {
    console.error('加载角色列表失败:', error)
  } finally {
    roleLoading.value = false
  }
}

// 计算属性
const canUndo = computed(() => historyIndex.value > 0)
const canRedo = computed(() => historyIndex.value < historyStack.value.length - 1)

// 拖拽开始
function onDragStart(event, nodeType) {
  event.dataTransfer.setData('nodeType', JSON.stringify(nodeType))
  event.dataTransfer.effectAllowed = 'copy'
}

// 放置节点
function onDrop(event) {
  const nodeTypeData = event.dataTransfer.getData('nodeType')
  if (!nodeTypeData) return

  const nodeType = JSON.parse(nodeTypeData)
  const rect = canvasContainerRef.value.getBoundingClientRect()
  const x = (event.clientX - rect.left - panOffset.x) / zoom.value
  const y = (event.clientY - rect.top - panOffset.y) / zoom.value

  const newNode = createNode(nodeType.type, x, y)
  saveHistory()
  nodes.value.push(newNode)
  selectedNode.value = newNode
}

// 创建节点
function createNode(type, x, y) {
  return {
    id: `node_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    type,
    code: `${type.toLowerCase()}_${nodes.value.length + 1}`,
    label: getDefaultNodeLabel(type),
    x: Math.round(x),
    y: Math.round(y),
    config: createDefaultConfig(type),
    branches: type === 'CONDITION' ? createDefaultBranches() : null
  }
}

// 获取默认节点标签
function getDefaultNodeLabel(type) {
  const labels = {
    START: '开始',
    APPROVAL: '审批节点',
    CONDITION: '条件分支',
    CC: '抄送',
    END: '结束'
  }
  return labels[type] || type
}

// 创建默认配置
function createDefaultConfig(type) {
  const config = {}
  if (type === 'APPROVAL') {
    config.approverMode = 'ROLE'
    config.approverValue = ''
    config.approvalMode = 'ANY'
    config.timeoutHours = 72
    config.allowReject = true
    config.allowDelegate = true
  } else if (type === 'CC') {
    config.approverMode = 'ROLE'
    config.approverValue = ''
    config.notifyMode = 'BOTH'
  }
  return config
}

// 创建默认分支
function createDefaultBranches() {
  return [
    {
      label: '分支1',
      condition: { field: 'amount', operator: 'GTE', value: '1000' },
      targetNodeId: null
    },
    {
      label: '分支2',
      condition: { field: 'amount', operator: 'LT', value: '1000' },
      targetNodeId: null
    }
  ]
}

// 节点鼠标按下
function onNodeMouseDown(event, node) {
  if (event.button !== 0) return // 只处理左键
  
  isDraggingNode.value = true
  dragNode.value = node
  dragStart.x = event.clientX
  dragStart.y = event.clientY

  document.addEventListener('mousemove', onNodeMouseMove)
  document.addEventListener('mouseup', onNodeMouseUp)
}

// 节点拖拽移动
function onNodeMouseMove(event) {
  if (!isDraggingNode.value || !dragNode.value) return

  const dx = (event.clientX - dragStart.x) / zoom.value
  const dy = (event.clientY - dragStart.y) / zoom.value

  dragNode.value.x = Math.round(dragNode.value.x + dx)
  dragNode.value.y = Math.round(dragNode.value.y + dy)

  dragStart.x = event.clientX
  dragStart.y = event.clientY
}

// 节点拖拽结束
function onNodeMouseUp() {
  if (isDraggingNode.value && dragNode.value) {
    saveHistory()
  }
  
  isDraggingNode.value = false
  dragNode.value = null
  document.removeEventListener('mousemove', onNodeMouseMove)
  document.removeEventListener('mouseup', onNodeMouseUp)
}

// 连接点鼠标按下
function onHandleMouseDown(event, node, handleType, branchIndex = null) {
  event.stopPropagation()
  
  console.log('开始连线:', node.label, handleType, branchIndex !== null ? `分支${branchIndex + 1}` : '')
  
  connectingNode.value = node
  connectingHandle.value = handleType

  tempEdge.value = {
    sourceNode: handleType === 'out' ? node : null,
    targetNode: handleType === 'in' ? node : null,
    branchIndex: branchIndex,
    mouseX: event.clientX,
    mouseY: event.clientY
  }

  document.addEventListener('mousemove', onHandleMouseMove)
  document.addEventListener('mouseup', onHandleMouseUp)
}

// 连接线拖拽移动
function onHandleMouseMove(event) {
  if (!tempEdge.value) return

  const rect = canvasContainerRef.value.getBoundingClientRect()
  tempEdge.value.mouseX = (event.clientX - rect.left - panOffset.x) / zoom.value
  tempEdge.value.mouseY = (event.clientY - rect.top - panOffset.y) / zoom.value
}

// 连接线拖拽结束
function onHandleMouseUp(event) {
  if (!tempEdge.value) return

  console.log('结束连线，目标:', event.target)

  // 查找目标节点 - 先找包含 data-node-id 的父元素
  const nodeContainer = event.target.closest('[data-node-id]')
  console.log('找到节点容器:', nodeContainer?.dataset?.nodeId)
  
  if (nodeContainer) {
    const targetNodeId = nodeContainer.dataset.nodeId
    const targetNode = nodes.value.find(n => n.id === targetNodeId)
    
    console.log('目标节点:', targetNode?.label)
    
    if (targetNode && targetNode.id !== connectingNode.value?.id) {
      // 创建连线
      const sourceNode = connectingHandle.value === 'out' ? connectingNode.value : targetNode
      const destNode = connectingHandle.value === 'out' ? targetNode : connectingNode.value
      const branchIndex = tempEdge.value.branchIndex

      console.log('创建连线:', sourceNode.label, '->', destNode.label, branchIndex !== null ? `(分支${branchIndex + 1})` : '')

      // 对于条件节点，检查该分支是否已有连线
      let canCreateEdge = true
      let warningMsg = ''
      
      if (sourceNode.type === 'CONDITION' && branchIndex !== null) {
        // 检查该分支是否已有连线
        const existingEdge = edges.value.find(e => 
          e.source === sourceNode.id && e.branchIndex === branchIndex
        )
        if (existingEdge) {
          canCreateEdge = false
          warningMsg = `分支${branchIndex + 1}已有连线，请先删除原连线`
        }
      } else {
        // 普通节点检查是否有重复连线
        const existingEdge = edges.value.find(e => 
          e.source === sourceNode.id && e.target === destNode.id
        )
        if (existingEdge) {
          canCreateEdge = false
          warningMsg = '该连线已存在'
        }
      }

      if (canCreateEdge) {
        saveHistory()
        const newEdge = {
          id: `edge_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          source: sourceNode.id,
          target: destNode.id,
          label: ''
        }
        
        // 如果是条件节点的分支连线，记录分支索引和标签
        if (sourceNode.type === 'CONDITION' && branchIndex !== null) {
          newEdge.branchIndex = branchIndex
          newEdge.label = sourceNode.branches[branchIndex]?.label || `分支${branchIndex + 1}`
          
          // 同时更新条件节点的分支目标
          if (sourceNode.branches[branchIndex]) {
            sourceNode.branches[branchIndex].targetNodeId = destNode.id
          }
        }
        
        edges.value.push(newEdge)
      } else {
        ElMessage.warning(warningMsg)
      }
    } else if (targetNode && targetNode.id === connectingNode.value?.id) {
      console.log('不能连接到自己')
    }
  } else {
    console.log('未找到目标节点')
  }

  tempEdge.value = null
  connectingNode.value = null
  connectingHandle.value = null
  document.removeEventListener('mousemove', onHandleMouseMove)
  document.removeEventListener('mouseup', onHandleMouseUp)
}

// 画布鼠标按下
function onCanvasMouseDown(event) {
  if (event.button !== 0) return
  // 支持点击拖拽模式按钮 或 按住空格键 + 鼠标拖拽
  const isPanMode = panMode.value || spaceKeyPressed
  if (!isPanMode) return
  // 检查是否点击在画布区域内
  if (!canvasContainerRef.value?.contains(event.target)) return

  isPanning.value = true
  panStart.x = event.clientX
  panStart.y = event.clientY

  document.addEventListener('mousemove', onCanvasMouseMove)
  document.addEventListener('mouseup', onCanvasMouseUp)
}

// 画布拖拽移动
function onCanvasMouseMove(event) {
  if (!isPanning.value) return

  const dx = event.clientX - panStart.x
  const dy = event.clientY - panStart.y

  panOffset.x += dx
  panOffset.y += dy

  panStart.x = event.clientX
  panStart.y = event.clientY
}

// 画布拖拽结束
function onCanvasMouseUp() {
  isPanning.value = false
  document.removeEventListener('mousemove', onCanvasMouseMove)
  document.removeEventListener('mouseup', onCanvasMouseUp)
}

// 画布滚轮事件 - 实现缩放和平移
function onCanvasWheel(event) {
  event.preventDefault()

  if (event.ctrlKey || event.metaKey) {
    // Ctrl + 滚轮 = 缩放
    event.preventDefault()
    const delta = event.deltaY > 0 ? -0.1 : 0.1
    zoom.value = Math.min(Math.max(zoom.value + delta, 0.3), 2)
  } else {
    // 普通滚轮 = 平移
    const deltaX = event.shiftKey ? event.deltaY : event.deltaX
    const deltaY = event.shiftKey ? event.deltaX : event.deltaY
    panOffset.x -= deltaX
    panOffset.y -= deltaY
  }
}

// 画布点击
function onCanvasClick(event) {
  if (event.target === canvasRef.value || event.target.tagName === 'svg' || event.target.tagName === 'rect') {
    selectedNode.value = null
    selectedEdge.value = null
  }
}

// 选择节点
function selectNode(node) {
  selectedNode.value = node
  selectedEdge.value = null
  closeContextMenu()

  // 加载用户和角色列表（用于审批/抄送节点）
  if (node.type === 'APPROVAL' || node.type === 'CC') {
    loadUserList()
    loadRoleList()
  }

  // 确保条件节点有branches数组
  if (node.type === 'CONDITION') {
    if (!node.branches) {
      node.branches = createDefaultBranches()
    } else {
      // 确保每个分支都有完整的结构
      node.branches.forEach(branch => {
        if (!branch.label) branch.label = ''
        if (!branch.condition) {
          branch.condition = { field: '', operator: 'EQ', value: '' }
        } else {
          if (!branch.condition.field) branch.condition.field = ''
          if (!branch.condition.operator) branch.condition.operator = 'EQ'
          if (!branch.condition.value) branch.condition.value = ''
        }
        if (!branch.targetNodeId) branch.targetNodeId = null
      })
    }
  }
}

// 选择连线
function selectEdge(edge) {
  selectedEdge.value = edge
  selectedNode.value = null
  closeContextMenu()
}

// 关闭属性面板
function closePropertyPanel() {
  selectedNode.value = null
  selectedEdge.value = null
}

// 删除节点
function deleteNode() {
  if (!selectedNode.value) return

  saveHistory()
  const nodeId = selectedNode.value.id
  
  // 清除所有条件节点中指向被删除节点的 targetNodeId
  nodes.value.forEach(node => {
    if (node.type === 'CONDITION' && node.branches) {
      node.branches.forEach(branch => {
        if (branch.targetNodeId === nodeId) {
          branch.targetNodeId = null
        }
      })
    }
  })
  
  nodes.value = nodes.value.filter(n => n.id !== nodeId)
  edges.value = edges.value.filter(e => e.source !== nodeId && e.target !== nodeId)
  selectedNode.value = null
  closeContextMenu()
  
  ElMessage.success('节点已删除')
}

// 删除连线
function deleteEdge() {
  if (!selectedEdge.value) return

  saveHistory()
  
  // 如果是条件分支的连线，清除分支的 targetNodeId
  if (selectedEdge.value.branchIndex !== undefined) {
    const sourceNode = nodes.value.find(n => n.id === selectedEdge.value.source)
    if (sourceNode && sourceNode.branches && sourceNode.branches[selectedEdge.value.branchIndex]) {
      sourceNode.branches[selectedEdge.value.branchIndex].targetNodeId = null
    }
  }
  
  edges.value = edges.value.filter(e => !isSameEdge(e, selectedEdge.value))
  selectedEdge.value = null
  closeContextMenu()
  
  ElMessage.success('连线已删除')
}

// 添加分支
function addBranch() {
  if (!selectedNode.value) return
  
  // 确保 branches 数组存在
  if (!selectedNode.value.branches) {
    selectedNode.value.branches = []
  }

  selectedNode.value.branches.push({
    label: `分支${selectedNode.value.branches.length + 1}`,
    condition: { field: '', operator: 'EQ', value: '' },
    targetNodeId: null
  })
}

// 删除分支
function removeBranch(index) {
  if (!selectedNode.value || !selectedNode.value.branches) return
  if (selectedNode.value.branches.length <= 1) {
    ElMessage.warning('至少保留一个分支')
    return
  }

  selectedNode.value.branches.splice(index, 1)
}

// 复制节点
function copyNode() {
  if (!selectedNode.value) return
  copiedNode.value = JSON.parse(JSON.stringify(selectedNode.value))
  ElMessage.success('节点已复制')
  closeContextMenu()
}

// 粘贴节点
function pasteNode() {
  if (!copiedNode.value) return

  saveHistory()
  const newNode = {
    ...JSON.parse(JSON.stringify(copiedNode.value)),
    id: `node_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    x: contextMenu.x - panOffset.x,
    y: contextMenu.y - panOffset.y
  }
  nodes.value.push(newNode)
  selectedNode.value = newNode
  closeContextMenu()
  
  ElMessage.success('节点已粘贴')
}

// 右键菜单
function onNodeContextMenu(event, node) {
  selectedNode.value = node
  contextMenu.visible = true
  contextMenu.x = event.clientX
  contextMenu.y = event.clientY
  contextMenu.type = 'node'
  contextMenu.target = node
}

function onEdgeContextMenu(event, edge) {
  selectedEdge.value = edge
  contextMenu.visible = true
  contextMenu.x = event.clientX
  contextMenu.y = event.clientY
  contextMenu.type = 'edge'
  contextMenu.target = edge
}

function onCanvasContextMenu(event) {
  if (event.target === canvasRef.value || event.target.tagName === 'svg') {
    contextMenu.visible = true
    contextMenu.x = event.clientX
    contextMenu.y = event.clientY
    contextMenu.type = 'canvas'
    contextMenu.target = null
  }
}

function closeContextMenu() {
  contextMenu.visible = false
  contextMenu.type = null
  contextMenu.target = null
}

// 缩放
function zoomIn() {
  zoom.value = Math.min(zoom.value + 0.1, 2)
}

function zoomOut() {
  zoom.value = Math.max(zoom.value - 0.1, 0.3)
}

function fitView() {
  if (nodes.value.length === 0) return

  const padding = 50
  const minX = Math.min(...nodes.value.map(n => n.x)) - padding
  const minY = Math.min(...nodes.value.map(n => n.y)) - padding
  const maxX = Math.max(...nodes.value.map(n => n.x + 160)) + padding
  const maxY = Math.max(...nodes.value.map(n => n.y + 80)) + padding

  const width = maxX - minX
  const height = maxY - minY

  const containerRect = canvasContainerRef.value.getBoundingClientRect()
  const scaleX = containerRect.width / width
  const scaleY = containerRect.height / height
  const newZoom = Math.min(scaleX, scaleY, 1)

  zoom.value = newZoom
  panOffset.x = -minX * newZoom + (containerRect.width - width * newZoom) / 2
  panOffset.y = -minY * newZoom + (containerRect.height - height * newZoom) / 2
}

// 切换拖拽模式
function togglePanMode() {
  panMode.value = !panMode.value
}

// 自动布局
function autoLayout() {
  if (nodes.value.length === 0) return

  saveHistory()

  // 简单的层级布局
  const startNode = nodes.value.find(n => n.type === 'START')
  if (!startNode) return

  const layers = []
  const visited = new Set()
  
  // BFS分层
  const queue = [{ node: startNode, layer: 0 }]
  visited.add(startNode.id)

  while (queue.length > 0) {
    const { node, layer } = queue.shift()
    
    if (!layers[layer]) layers[layer] = []
    layers[layer].push(node)

    const outEdges = edges.value.filter(e => e.source === node.id)
    outEdges.forEach(edge => {
      const targetNode = nodes.value.find(n => n.id === edge.target)
      if (targetNode && !visited.has(targetNode.id)) {
        visited.add(targetNode.id)
        queue.push({ node: targetNode, layer: layer + 1 })
      }
    })
  }

  // 布局节点
  const layerSpacing = 250
  const nodeSpacing = 150
  const startX = 100
  const startY = 100

  layers.forEach((layerNodes, layerIndex) => {
    const y = startY + layerIndex * layerSpacing
    const totalWidth = (layerNodes.length - 1) * nodeSpacing
    const x = startX

    layerNodes.forEach((node, nodeIndex) => {
      node.x = x + nodeIndex * nodeSpacing - totalWidth / 2 + 300
      node.y = y
    })
  })

  ElMessage.success('自动布局完成')
  closeContextMenu()
}

// 历史记录
function saveHistory() {
  historyStack.value = historyStack.value.slice(0, historyIndex.value + 1)
  historyStack.value.push(JSON.stringify({ nodes: nodes.value, edges: edges.value }))
  historyIndex.value = historyStack.value.length - 1

  // 限制历史记录数量
  if (historyStack.value.length > 50) {
    historyStack.value.shift()
    historyIndex.value--
  }
}

function undo() {
  if (!canUndo.value) return
  historyIndex.value--
  const state = JSON.parse(historyStack.value[historyIndex.value])
  nodes.value = state.nodes
  edges.value = state.edges
  selectedNode.value = null
  selectedEdge.value = null
}

function redo() {
  if (!canRedo.value) return
  historyIndex.value++
  const state = JSON.parse(historyStack.value[historyIndex.value])
  nodes.value = state.nodes
  edges.value = state.edges
  selectedNode.value = null
  selectedEdge.value = null
}

// 应用表单字段到条件选项
function applyFormFields() {
  if (!formDesignerRef.value) return
  
  const fieldList = formDesignerRef.value.getFieldList()
  if (fieldList.length === 0) {
    ElMessage.warning('请先添加表单字段')
    return
  }
  
  // 更新可用字段列表
  availableFields.value = fieldList.map(field => ({
    key: field.key,
    label: field.label,
    type: field.type
  }))
  
  // 同时更新表单数据
  workflowForm.value = formDesignerRef.value.getFormData()
  
  formDesignerVisible.value = false
  ElMessage.success(`已应用 ${fieldList.length} 个表单字段，现在可以在条件分支中使用这些字段`)
  
  console.log('可用字段已更新:', availableFields.value)
}

// 表单保存回调
function onFormSave(formData) {
  workflowFormData.form = formData
  console.log('表单数据已保存:', formData)

  // 自动同步字段到可用列表
  if (formData && formData.fields) {
    availableFields.value = formData.fields.map(field => ({
      key: field.key,
      label: field.label,
      type: field.type
    }))
    console.log('字段已自动同步:', availableFields.value)

    // 如果当前已选中条件节点，刷新属性面板以更新字段列表
    nextTick(() => {
      if (selectedNode.value && selectedNode.value.type === 'CONDITION') {
        // 保存当前选中的节点ID
        const currentNodeId = selectedNode.value.id
        // 先清空选择，触发面板关闭
        selectedNode.value = null
        // 下一帧重新选中节点，触发面板重新渲染
        nextTick(() => {
          const node = nodes.value.find(n => n.id === currentNodeId)
          if (node) {
            selectedNode.value = node
          }
        })
      }
      console.log('可用字段列表:', availableFields.value)
    })
  }
}

// 验证流程配置
function validateFlowConfig() {
  const errors = []

  // 检查是否有开始节点
  const startNodes = nodes.value.filter(n => n.type === 'START')
  if (startNodes.length === 0) {
    errors.push('流程必须包含一个开始节点')
  } else if (startNodes.length > 1) {
    errors.push('流程只能有一个开始节点')
  }

  // 检查是否有结束节点
  const endNodes = nodes.value.filter(n => n.type === 'END')
  if (endNodes.length === 0) {
    errors.push('流程必须包含至少一个结束节点')
  }

  // 检查节点配置
  nodes.value.forEach(node => {
    if (node.type === 'APPROVAL') {
      if (!node.config.approverValue) {
        errors.push(`审批节点"${node.label}"未配置审批人`)
      }
    }
    if (node.type === 'CONDITION') {
      if (!node.branches || node.branches.length < 2) {
        errors.push(`条件节点"${node.label}"至少需要2个分支`)
      }
    }
    if (node.type === 'CC') {
      if (!node.config.approverValue) {
        errors.push(`抄送节点"${node.label}"未配置抄送人`)
      }
    }
  })

  // 检查孤立节点
  nodes.value.forEach(node => {
    if (node.type !== 'START' && node.type !== 'END') {
      const hasIncoming = edges.value.some(e => e.target === node.id)
      const hasOutgoing = edges.value.some(e => e.source === node.id)
      if (!hasIncoming && !hasOutgoing) {
        errors.push(`节点"${node.label}"没有连接到流程中`)
      }
    }
  })

  return errors
}

// 保存
async function save() {
  // 先验证基本信息
  if (!workflowFormData.code) {
    ElMessage.error('请填写流程编码')
    currentStep.value = 0
    return
  }
  if (!workflowFormData.name) {
    ElMessage.error('请填写流程名称')
    currentStep.value = 0
    return
  }
  if (!workflowFormData.categoryId) {
    ElMessage.error('请选择流程分类')
    currentStep.value = 0
    return
  }

  // 验证流程配置
  const flowErrors = validateFlowConfig()
  if (flowErrors.length > 0) {
    ElMessage.error(`流程验证失败：${flowErrors[0]}`)
    currentStep.value = 2
    return
  }

  saving.value = true
  try {
    // 同步流程图数据
    workflowFormData.nodes = nodes.value
    workflowFormData.edges = edges.value

    // 构建接口参数
    const saveData = {
      templateCode: workflowFormData.code,
      templateName: workflowFormData.name,
      description: workflowFormData.description || '',
      categoryId: workflowFormData.categoryId,
      formSchema: JSON.stringify(workflowFormData.form),
      graphData: JSON.stringify({
        nodes: nodes.value,
        edges: edges.value
      })
    }

    console.log('保存数据:', saveData)

    // 判断是新增还是更新
    if (workflowId.value) {
      // 更新已有流程
      await updateTemplate({
        templateId: workflowId.value,
        ...saveData
      })
      ElMessage.success('更新成功')
      router.push('/workflow/list')
    } else {
      // 创建新流程
      await createTemplate(saveData)
      ElMessage.success('保存成功')
      router.push('/workflow/list')
    }
  } catch (error) {
    console.error('保存失败:', error)
    ElMessage.error(error.message || '保存失败')
  } finally {
    saving.value = false
  }
}

// 获取连线路径
function getEdgePath(edge) {
  const sourceNode = nodes.value.find(n => n.id === edge.source)
  const targetNode = nodes.value.find(n => n.id === edge.target)
  if (!sourceNode || !targetNode) return ''

  const sx = sourceNode.x + getNodeWidth(sourceNode) / 2
  const sy = sourceNode.y + 40
  const tx = targetNode.x + getNodeWidth(targetNode) / 2
  const ty = targetNode.y + 40

  const midX = (sx + tx) / 2

  return `M ${sx} ${sy} C ${midX} ${sy}, ${midX} ${ty}, ${tx} ${ty}`
}

// 获取临时连线路径
function getTempEdgePath() {
  if (!tempEdge.value) return ''

  const node = connectingNode.value
  const nodeX = node.x + getNodeWidth(node) / 2
  const nodeY = node.y + 40

  const mouseX = tempEdge.value.mouseX
  const mouseY = tempEdge.value.mouseY

  const midX = (nodeX + mouseX) / 2

  if (connectingHandle.value === 'out') {
    return `M ${nodeX} ${nodeY} C ${midX} ${nodeY}, ${midX} ${mouseY}, ${mouseX} ${mouseY}`
  } else {
    return `M ${mouseX} ${mouseY} C ${midX} ${mouseY}, ${midX} ${nodeY}, ${nodeX} ${nodeY}`
  }
}

// 获取连线标签位置
function getEdgeLabelPosition(edge) {
  const sourceNode = nodes.value.find(n => n.id === edge.source)
  const targetNode = nodes.value.find(n => n.id === edge.target)
  if (!sourceNode || !targetNode) return { x: 0, y: 0 }

  const sx = sourceNode.x + getNodeWidth(sourceNode) / 2
  const sy = sourceNode.y + 40
  const tx = targetNode.x + getNodeWidth(targetNode) / 2
  const ty = targetNode.y + 40

  return {
    x: (sx + tx) / 2,
    y: (sy + ty) / 2
  }
}

// 节点样式
function getNodeClass(node) {
  const classes = {
    START: 'border-green-500',
    APPROVAL: 'border-blue-500',
    CONDITION: 'border-orange-500',
    CC: 'border-purple-500',
    END: 'border-red-500'
  }
  return classes[node.type] || 'border-slate-300'
}

function getNodeHeaderClass(node) {
  const classes = {
    START: 'bg-green-500 text-white',
    APPROVAL: 'bg-blue-500 text-white',
    CONDITION: 'bg-orange-500 text-white',
    CC: 'bg-purple-500 text-white',
    END: 'bg-red-500 text-white'
  }
  return classes[node.type] || 'bg-slate-500 text-white'
}

function getNodeIcon(type) {
  const node = nodeTypes.find(n => n.type === type)
  return node ? node.icon : Document
}

function getNodeTypeLabel(type) {
  const node = nodeTypes.find(n => n.type === type)
  return node ? node.label : type
}

function getNodeWidth(node) {
  return node.type === 'CONDITION' ? 180 : 160
}

function hasNodeContent(node) {
  return ['APPROVAL', 'CONDITION', 'CC'].includes(node.type)
}

function getNodeLabel(nodeId) {
  const node = nodes.value.find(n => n.id === nodeId)
  return node ? node.label : '未知节点'
}

// 获取分支连接点位置
function getBranchHandleStyle(node, branchIndex) {
  const totalBranches = node.branches?.length || 1
  const nodeHeight = 60 + (hasNodeContent(node) ? 40 : 0)
  
  // 计算每个分支连接点的垂直位置
  const spacing = nodeHeight / (totalBranches + 1)
  const top = spacing * (branchIndex + 1)
  
  return {
    top: `${top}px`,
    right: '-7px'
  }
}

// 节点文本
function getApproverText(node) {
  const modeLabels = {
    USER: '指定用户',
    ROLE: '指定角色',
    DEPT_LEADER: '部门负责人',
    INITIATOR_MANAGER: '发起人主管'
  }
  return modeLabels[node.config.approverMode] || '未配置'
}

function getCcText(node) {
  const modeLabels = {
    USER: '指定用户',
    ROLE: '指定角色',
    DEPT_LEADER: '部门负责人',
    DYNAMIC: '动态指定'
  }
  return modeLabels[node.config.approverMode] || '未配置'
}

// 节点背景样式
function getNodeBgClass(type) {
  const classes = {
    START: 'bg-emerald-100',
    APPROVAL: 'bg-blue-100',
    CONDITION: 'bg-amber-100',
    CC: 'bg-violet-100',
    END: 'bg-rose-100'
  }
  return classes[type] || 'bg-slate-100'
}

// 节点图标样式
function getNodeIconClass(type) {
  const classes = {
    START: 'text-emerald-600',
    APPROVAL: 'text-blue-600',
    CONDITION: 'text-amber-600',
    CC: 'text-violet-600',
    END: 'text-rose-600'
  }
  return classes[type] || 'text-slate-600'
}

// 状态标签
function getStatusType(status) {
  const types = {
    draft: 'info',
    published: 'success',
    disabled: 'danger'
  }
  return types[status] || 'info'
}

function getStatusLabel(status) {
  const labels = {
    draft: '草稿',
    published: '已发布',
    disabled: '已停用'
  }
  return labels[status] || status
}

const loading = ref(false)

// 加载流程详情
async function loadWorkflow() {
  if (!workflowId.value) return

  loading.value = true
  try {
    const data = await getTemplateDetail(workflowId.value)
    console.log('加载流程数据:', data)

    // 回填基本信息
    workflowFormData.code = data.templateCode || ''
    workflowFormData.name = data.templateName || ''
    workflowFormData.categoryId = data.categoryId || null
    workflowFormData.description = data.description || ''

    // 回填表单设计
    if (data.formSchema) {
      try {
        const formSchema = typeof data.formSchema === 'string' ? JSON.parse(data.formSchema) : data.formSchema
        workflowFormData.form = formSchema || { name: '', fields: [] }

        // 同步可用字段
        if (formSchema && formSchema.fields) {
          availableFields.value = formSchema.fields.map(field => ({
            key: field.key,
            label: field.label,
            type: field.type
          }))
          console.log('已加载表单字段:', availableFields.value)
        }
      } catch (e) {
        console.error('解析表单schema失败:', e)
        workflowFormData.form = { name: '', fields: [] }
      }
    }

    // 回填流程图数据
    if (data.graphData) {
      try {
        const graphData = typeof data.graphData === 'string' ? JSON.parse(data.graphData) : data.graphData
        nodes.value = graphData.nodes || []
        edges.value = normalizeEdges(graphData.edges || [])
        console.log('已加载流程图节点:', nodes.value.length, '连线:', edges.value.length)
      } catch (e) {
        console.error('解析流程图数据失败:', e)
        // 如果解析失败，初始化默认的开始和结束节点
        nodes.value = [
          createNode('START', 150, 150),
          createNode('END', 800, 150)
        ]
        edges.value = []
      }
    }

    // 更新流程状态
    workflowStatus.value = data.status || 'draft'

    // 强制刷新表单设计器以回显数据
    formDesignerKey.value++

    // 如果有mode参数且为view，设置只读模式
    const mode = route.query.mode
    if (mode === 'view') {
      ElMessage.info('当前为查看模式，无法编辑')
    }
  } catch (error) {
    console.error('加载流程数据失败:', error)
    ElMessage.error(error.message || '加载流程数据失败')
  } finally {
    loading.value = false
  }
}

// 点击外部关闭菜单
function handleClickOutside() {
  if (contextMenu.visible) {
    closeContextMenu()
  }
}

// 初始化
onMounted(async () => {
  // 加载分类列表
  await loadCategoryList()

  // 预加载用户和角色列表（用于审批/抄送节点）
  loadUserList()
  loadRoleList()

  // 初始化开始和结束节点（仅在新建流程时）
  if (!workflowId.value && nodes.value.length === 0) {
    nodes.value = [
      createNode('START', 150, 150),
      createNode('END', 800, 150)
    ]
    // 保存初始状态
    saveHistory()
  }

  // 监听点击外部
  document.addEventListener('click', handleClickOutside)

  // 监听空格键状态 - 用于拖拽画布
  document.addEventListener('keydown', onKeyDown)
  document.addEventListener('keyup', onKeyUp)
  window.addEventListener('blur', onWindowBlur)

  // 如果有id参数,加载流程数据
  if (workflowId.value) {
    await loadWorkflow()
    // 加载后保存初始状态用于撤销
    saveHistory()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', onKeyDown)
  document.removeEventListener('keyup', onKeyUp)
  window.removeEventListener('blur', onWindowBlur)
  document.removeEventListener('mousemove', onNodeMouseMove)
  document.removeEventListener('mouseup', onNodeMouseUp)
  document.removeEventListener('mousemove', onHandleMouseMove)
  document.removeEventListener('mouseup', onHandleMouseUp)
  document.removeEventListener('mousemove', onCanvasMouseMove)
  document.removeEventListener('mouseup', onCanvasMouseUp)
})
</script>

<style scoped>
.workflow-designer {
  user-select: none;
}

.flow-node {
  cursor: move;
  position: relative;
  min-width: 160px;
  transform-origin: center center;
}

.node-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: white;
  border: 2.5px solid #3b82f6;
  cursor: crosshair;
  z-index: 10;
  transition: all 0.15s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.node-handle:hover {
  width: 16px;
  height: 16px;
  background: #3b82f6;
  border-color: #2563eb;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transform: scale(1.15);
}

.node-handle-in {
  left: -5px;
  top: 50%;
  transform: translateY(-50%);
}

.node-handle-in:hover {
  transform: translateY(-50%) scale(1.15);
}

.node-handle-out {
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
}

.node-handle-out:hover {
  transform: translateY(-50%) scale(1.15);
}

/* 分支连接点样式 */
.node-handle-branch {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  border: 2.5px solid #f59e0b;
  cursor: crosshair;
  z-index: 10;
  transition: all 0.15s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-handle-branch:hover {
  width: 22px;
  height: 22px;
  background: #f59e0b;
  border-color: #d97706;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  transform: scale(1.15);
}

.branch-handle-label {
  font-size: 9px;
  font-weight: bold;
  color: #f59e0b;
  pointer-events: none;
}

.node-handle-branch:hover .branch-handle-label {
  color: white;
}

.flow-edge {
  transition: stroke 0.2s, stroke-width 0.2s;
}

/* 右键菜单动画 */
.context-menu-enter-active,
.context-menu-leave-active {
  transition: all 0.15s ease;
}

.context-menu-enter-from,
.context-menu-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
